import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Copy, Pencil, RotateCcw, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import type { MapaCargo } from '@/components/Configuracao/mapaCargos/mapaCargosTypes'
import type { MapaCargosModalTab } from '@/hooks/useMapaCargosTab'
import {
  countMapaCargoAtividades,
  countMapaCargoSecoes,
} from '@/components/Configuracao/mapaCargos/mapaCargosListUtils'
import { MapaCargosEscopoEditor } from '@/components/Configuracao/mapaCargos/MapaCargosEscopoEditor'
import { MapaCargosEscopoView } from '@/components/Configuracao/mapaCargos/MapaCargosEscopoView'
import type { useMapaCargosEscopoEdit } from '@/hooks/useMapaCargosOverrides'

type EscopoEditState = ReturnType<typeof useMapaCargosEscopoEdit>

type Props = {
  cargo: MapaCargo | null
  modalTab: MapaCargosModalTab
  onTabChange: (tab: MapaCargosModalTab) => void
  onClose: () => void
  onCopy: (cargo: MapaCargo) => void
  canEditEscopo: boolean
  escopoEdit: EscopoEditState
}

export function MapaCargosDetailModal({
  cargo,
  modalTab,
  onTabChange,
  onClose,
  onCopy,
  canEditEscopo,
  escopoEdit,
}: Props) {
  if (!cargo) return null

  const displayCargo =
    escopoEdit.isEditing && modalTab === 'escopo'
      ? { ...cargo, secoes: escopoEdit.draftSecoes }
      : cargo

  return createPortal(
    <AnimatePresence>
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='absolute inset-0 bg-black/60 backdrop-blur-sm'
          onClick={onClose}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          className='relative z-10 flex max-h-[90vh] w-full max-w-[960px] flex-col overflow-hidden rounded-xl border border-[var(--glass-border)] bg-[var(--bg-card)] shadow-2xl'
        >
          <div className='flex items-center justify-between border-b border-[var(--glass-border)] px-6 py-4'>
            <div>
              <h3
                className='font-semibold text-[var(--font-color-h3)]'
                style={{ fontSize: 'var(--font-size-h3)' }}
              >
                Mapa do cargo: {cargo.titulo}
              </h3>
              {escopoEdit.hasOverride && modalTab === 'escopo' && !escopoEdit.isEditing && (
                <p className='mt-1 text-[12px] text-[var(--text-muted)]'>Escopo personalizado</p>
              )}
            </div>
            <button
              type='button'
              onClick={onClose}
              className='rounded-md p-1 text-[var(--text-muted)] transition-colors hover:bg-[var(--glass-bg)] hover:text-[var(--text-primary)]'
            >
              <X size={20} />
            </button>
          </div>

          <div className='flex gap-6 border-b border-[var(--glass-border)] px-6'>
            {(['info', 'escopo'] as const).map((tab) => (
              <button
                key={tab}
                type='button'
                onClick={() => onTabChange(tab)}
                className={cn(
                  'cursor-pointer border-b-2 py-3 text-[13px] font-semibold transition-colors',
                  modalTab === tab
                    ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]'
                    : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]',
                )}
              >
                {tab === 'info' ? 'Informações do cargo' : 'Escopo e atividades'}
              </button>
            ))}
          </div>

          <div className='min-h-0 flex-1 overflow-y-auto px-6 py-5'>
            {modalTab === 'info' ? (
              <div className='grid gap-4 sm:grid-cols-2'>
                <InfoField label='Cargo' value={cargo.titulo} />
                <InfoField label='Setor' value={cargo.setor} />
                <InfoField label='Blocos de responsabilidade' value={String(countMapaCargoSecoes(displayCargo))} />
                <InfoField label='Total de atividades' value={String(countMapaCargoAtividades(displayCargo))} />
              </div>
            ) : escopoEdit.isEditing ? (
              <MapaCargosEscopoEditor
                secoes={escopoEdit.draftSecoes}
                onChange={escopoEdit.setDraftSecoes}
              />
            ) : (
              <MapaCargosEscopoView secoes={cargo.secoes} />
            )}
          </div>

          <div className='flex flex-wrap items-center justify-between gap-2 border-t border-[var(--glass-border)] px-6 py-4'>
            <div className='flex flex-wrap gap-2'>
              {modalTab === 'escopo' && canEditEscopo && !escopoEdit.isEditing && (
                <Button type='button' variant='secondary' icon={<Pencil size={14} />} onClick={escopoEdit.startEdit}>
                  Editar escopo
                </Button>
              )}
              {modalTab === 'escopo' && escopoEdit.isEditing && escopoEdit.hasOverride && (
                <Button
                  type='button'
                  variant='secondary'
                  icon={<RotateCcw size={14} />}
                  loading={escopoEdit.saving}
                  onClick={() => void escopoEdit.resetToDefault()}
                >
                  Restaurar padrão
                </Button>
              )}
            </div>

            <div className='flex flex-wrap justify-end gap-2'>
              {escopoEdit.isEditing ? (
                <>
                  <Button type='button' variant='secondary' onClick={escopoEdit.cancelEdit} disabled={escopoEdit.saving}>
                    Cancelar
                  </Button>
                  <Button type='button' variant='primary' loading={escopoEdit.saving} onClick={() => void escopoEdit.saveEdit()}>
                    Salvar escopo
                  </Button>
                </>
              ) : (
                <>
                  <Button type='button' variant='secondary' onClick={onClose}>
                    Fechar
                  </Button>
                  <Button
                    type='button'
                    variant='primary'
                    icon={<Copy size={14} />}
                    onClick={() => onCopy(displayCargo)}
                  >
                    Copiar descrição
                  </Button>
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body,
  )
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className='rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] p-4'>
      <p
        className='font-medium uppercase tracking-wider text-[var(--font-color-label)]'
        style={{ fontSize: 'var(--font-size-label)' }}
      >
        {label}
      </p>
      <p className='mt-2 text-[13px] font-semibold text-[var(--text-primary)]'>{value}</p>
    </div>
  )
}
